import { useMutation } from '@tanstack/react-query'
import axios from 'axios'
import React from 'react'
import { useDispatch } from 'react-redux'
import {
  getInvitedPeople,
  removeOrganizationInvite,
} from 'src/app/PeopleInvitedToOrg/redux/peopleInvitedToOrg.actions'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import { OrganizationInviteResponse } from 'src/models/organizations.models'
import { OrgInviteStatus } from 'src/models/people.models'
import { connectedPush } from 'src/navigation/Lnk'
import { homeUrl } from 'src/navigation/urls'
import { getRequestConfig } from 'src/queries/requestConfig'
import { getMe } from 'src/redux/actions/auth.actions'
import {
  getOrganizationInvites,
  getOrganizations,
  getUserOrganizationInvites,
  PUT_ORGANIZATION_INVITE_FAILURE,
  PUT_ORGANIZATION_INVITE_INIT,
  PUT_ORGANIZATION_INVITE_SUCCESS,
  PutOrganizationInviteErrorPayload,
  PutOrganizationInviteSuccessPayload,
} from 'src/redux/actions/data/organizations.actions'

type BackendErrorResponse = {
  response: { data: PutOrganizationInviteErrorPayload }
}

type PutOrganizationInviteVariables = {
  status: OrgInviteStatus
  organizationInvite: OrganizationInviteResponse
  toast?: React.ReactElement
  setHasAcceptedInvite?: (val: boolean) => void
  onSignup?: boolean
}

/** Accept, reject, or cancel an invite while preserving legacy reducer state. */
export const usePutOrganizationInvite = () => {
  const dispatch = useDispatch()

  return useMutation({
    mutationFn: async ({
      status,
      organizationInvite,
      toast,
      setHasAcceptedInvite,
      onSignup,
    }: PutOrganizationInviteVariables): Promise<void> => {
      dispatch({ type: PUT_ORGANIZATION_INVITE_INIT })
      if (organizationInvite) {
        return axios
          .put<PutOrganizationInviteSuccessPayload>(
            `/organizationinvites/${organizationInvite.id}`,
            { status },
            getRequestConfig(),
          )
          .then(
            (response) => {
              const acceptedInvite = status === OrgInviteStatus.Accepted
              dispatch({
                type: PUT_ORGANIZATION_INVITE_SUCCESS,
                payload: response.data,
              })
              if (acceptedInvite) {
                dispatch(getMe())
                dispatch(getOrganizations())
              } else {
                // canceled or rejected
                dispatch(
                  getUserOrganizationInvites({
                    sortDirection: 'ASC',
                    sortField: 'createdAt',
                  }),
                )
                dispatch(
                  removeOrganizationInvite(response.data.organizationInvite.id),
                )
                dispatch(
                  getInvitedPeople(organizationInvite.organization.id, {
                    page: 0,
                    count: 10,
                  }),
                )
                if (toast) {
                  dispatch(
                    createSetIsToastOpen(true, {
                      toastMessage: toast,
                    }),
                  )
                }
              }
              if (setHasAcceptedInvite) {
                setHasAcceptedInvite(acceptedInvite)
              }
              if (onSignup) {
                dispatch(connectedPush(homeUrl()))
              }
              dispatch(getOrganizationInvites())
            },
            (err: BackendErrorResponse) => {
              dispatch({
                type: PUT_ORGANIZATION_INVITE_FAILURE,
                payload: err.response.data,
              })
            },
          )
      }
    },
  })
}
